import { isPlatformBrowser } from "@angular/common";
import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import * as Sentry from "@sentry/angular";
import { expireCookieSession, hasCookieSession, withoutSessionToken } from "../utils/auth-session";
export enum LoggedInUserKeys {
  LOGGED_IN_USER = "loggedInUser",
  BASIC_DETAILS = "basicDetails",
  USER_DETAILS = "userDetails",
  SCHEDULE_DETAILS = "schedule details",
  DRIVE_DETAILS = "driveDetails",
  SECTION_TIMER="sectionTimer"
}
@Injectable({
  providedIn: "root",
})
export class LoggedInUserService {
  constructor(@Inject(PLATFORM_ID) private platformId: object) { }

  getLoggedInUser(key: LoggedInUserKeys) {
    if (!isPlatformBrowser(this.platformId)) return "";
    // A hand-edited or corrupted value reads as signed out instead of throwing
    // inside every guard and interceptor.
    try {
      return JSON.parse(localStorage.getItem(!key ? LoggedInUserKeys.LOGGED_IN_USER : key));
    } catch {
      return null;
    }
  }

  setLoggedInUser(key: LoggedInUserKeys, localStorageData) {
    const storageKey = key ? key : LoggedInUserKeys.LOGGED_IN_USER;
    // With a cookie session the token stays in the HttpOnly cookie, never in storage.
    const data = storageKey === LoggedInUserKeys.LOGGED_IN_USER
      ? withoutSessionToken(localStorageData)
      : localStorageData;
    localStorage.setItem(storageKey, JSON.stringify(data));
  }
  removeLoggedInUser(key: LoggedInUserKeys) {
    localStorage.removeItem(key ? key : '');
  }

  /** Signed in: a stored profile plus either a cookie session or a legacy stored token. */
  isLoggedIn(): boolean {
    const user = this.getLoggedInUser(LoggedInUserKeys.LOGGED_IN_USER);
    return !!user && (!!user.authToken || hasCookieSession());
  }

  /** Bearer token still kept in storage (header mode); null once the session is in the cookie. */
  getLegacyToken(): string | null {
    const user = this.getLoggedInUser(LoggedInUserKeys.LOGGED_IN_USER);
    return user?.authToken || null;
  }

  clearEntireStorage() {
    try {
      Sentry.setUser(null);
    } catch {
      // Never let telemetry cleanup block a logout.
    }
    localStorage.clear();
    sessionStorage.clear();
    expireCookieSession();
  }
}
